import React, { useState } from "react";

import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";

import { Button, BackLink } from "ds/Button";
import * as Sticker from "ds/Sticker";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { capitalize } from "Libs/utils";
import { gitProjectSelector } from "Reducers/project/project";
import {
  addCertificate,
  cancelAddCertificate,
  certificateSelector
} from "Reducers/project/settings/certificate";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import * as S from "./CertificateForm.styles";

interface NewCertificate {
  key: string;
  certificate: string;
  chain: string;
}

const CertificateForm = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { organizationId, projectId } = useParams<{
    organizationId: string;
    projectId: string;
  }>();

  const project = useSelectorWithUrlParams(gitProjectSelector);
  const { error, updateLoading } = useAppSelector(certificateSelector);

  const [certificate, setCertificate] = useState<NewCertificate>({
    key: "",
    certificate: "",
    chain: ""
  });

  const handleOnChange: React.ChangeEventHandler<HTMLTextAreaElement> = e => {
    setCertificate({ ...certificate, [e.target.name]: e.target.value });
  };

  const handleSave = async ({
    certificate,
    chain,
    key
  }: {
    certificate: string;
    chain: string;
    key: string;
  }) => {
    if (!organizationId) return;

    let chainArray: string[] = [];
    if (chain && typeof chain === "string") {
      const split = chain.replace(
        /-----END CERTIFICATE-----/g,
        "-----END CERTIFICATE-----___SPLIT___"
      );
      chainArray = split.split("___SPLIT___");
      chainArray.splice(-1, 1);
    }
    const newCertificate = { key, certificate, chain: chainArray };

    if (!project) throw new Error("Project not found.");

    const result = await dispatch(
      addCertificate({
        organizationDescriptionId: organizationId,
        project,
        certificate: newCertificate
      })
    );
    if (result.type.includes("rejected")) return;
    navigate(`/${organizationId}/${projectId}/-/settings/certificates`);
  };

  return (
    <S.Layout>
      <S.Container>
        <BackLink
          id="project-certificate-back-button"
          from={`/${organizationId}/${projectId}/-/settings/certificates/add`}
          variant="link"
          to={`/${organizationId}/${projectId}/-/settings/certificates`}
          capitalized={false}
        >
          {intl.formatMessage({ id: "certificates.back_to" })}
        </BackLink>
        <S.Header>
          {intl.formatMessage({ id: "certificates.add_certificate" })}
        </S.Header>
        <S.CertificateFormLayout
          id="certificate-add-form"
          aria-labelledby="add-certificate-heading"
          onSubmit={e => {
            e.preventDefault();
            handleSave(certificate);
          }}
        >
          {error?.message && (
            <Sticker.Root variant="long" priority="critical">
              <Sticker.Icon priority="critical" />
              <Sticker.Text>{error.message}</Sticker.Text>
            </Sticker.Root>
          )}
          <S.Textarea
            id="project-certificate-private-key-input"
            label={`${intl.formatMessage({ id: "private_key" })}`}
            placeholder="Enter private key here..."
            value={certificate.key || ""}
            name="key"
            onChange={handleOnChange}
            error={error?.detail?.key}
            required={true}
            height={240}
          />
          <S.Textarea
            id="project-certificate-public-key-input"
            label={`${intl.formatMessage({ id: "public_key" })}`}
            placeholder="Enter public key here..."
            value={certificate.certificate || ""}
            name="certificate"
            onChange={handleOnChange}
            error={error?.detail?.certificate}
            required={true}
            height={240}
          />
          <S.Textarea
            id="project-certificate-intermediate-input"
            label={`${intl.formatMessage({
              id: "ssl_chain"
            })}`}
            placeholder="Enter intermediate SSL certificates here..."
            value={certificate.chain}
            name="chain"
            onChange={handleOnChange}
            required={false}
            height={240}
          />
          {updateLoading ? (
            <S.Loading />
          ) : (
            <S.ButtonWrapper>
              <Button
                analyticId="add_certificate"
                id="project-certificate-save-btn"
                type="submit"
                disabled={!certificate.key || !certificate.certificate}
              >
                {capitalize(intl.formatMessage({ id: "add_certificate" }))}
              </Button>
              <Button
                id="project-certificate-cancel-btn"
                variant="secondary"
                analyticId="cancel"
                onClick={() => dispatch(cancelAddCertificate())}
                to={`/${organizationId}/${projectId}/-/settings/certificates`}
              >
                {capitalize(intl.formatMessage({ id: "cancel" }))}
              </Button>
            </S.ButtonWrapper>
          )}
        </S.CertificateFormLayout>
      </S.Container>
    </S.Layout>
  );
};

export default CertificateForm;
